import React, { useState, useEffect, useRef } from 'react';

function getStats() {
  try {
    const s = localStorage.getItem('spyq_stats');
    return s ? JSON.parse(s) : null;
  } catch { return null; }
}

function Home({ ws, sendMessage, error, clearError, initialRoomCode, savedName, onSaveName }) {
  const [mode, setMode] = useState(initialRoomCode ? 'join' : null);
  const [playerName, setPlayerName] = useState(savedName || '');
  const [joinCode, setJoinCode] = useState(initialRoomCode || '');
  const [liveStats, setLiveStats] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackList, setFeedbackList] = useState(null);
  const longPressTimer = useRef(null);
  const [dashTab, setDashTab] = useState('live');

  // PWA install — capture Chrome's native prompt and detect iOS/standalone
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const triggerInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      try { await installPrompt.userChoice; } catch {}
      setInstallPrompt(null);
    } else {
      setShowInstallHelp(true);
    }
  };

  useEffect(() => {
    const fetchStats = () => {
      fetch('/api/stats').then((r) => r.json()).then(setLiveStats).catch(() => {});
    };
    fetchStats();
    const interval = setInterval(fetchStats, 15000);
    return () => clearInterval(interval);
  }, []);

  const onLogoTouchStart = () => {
    longPressTimer.current = setTimeout(() => setShowAdmin(true), 2000);
  };
  const onLogoTouchEnd = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  const handleCreate = () => {
    if (!playerName.trim()) return;
    clearError();
    if (onSaveName) onSaveName(playerName.trim());
    sendMessage('CREATE_ROOM', { playerName: playerName.trim() });
  };

  const handleJoin = () => {
    if (!playerName.trim() || !joinCode.trim()) return;
    clearError();
    if (onSaveName) onSaveName(playerName.trim());
    sendMessage('JOIN_ROOM', {
      playerName: playerName.trim(),
      roomCode: joinCode.trim().toUpperCase(),
    });
  };

  if (!ws) {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        <div className="logo">SpyQ</div>
        <div style={{ margin: '24px 0' }}>
          <div style={{
            width: '36px', height: '36px', border: '3px solid rgba(139,92,246,0.2)',
            borderTop: '3px solid #8B5CF6', borderRadius: '50%',
            animation: 'spin 1s linear infinite', margin: '0 auto 14px',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: 'var(--ink-2)', fontWeight: 600 }}>מתחבר לשרת...</p>
        </div>
      </div>
    );
  }

  if (showAdmin) {
    const hist = liveStats?.historical;
    const modeNames = { spyq: 'SpyQ 🕵️', thatsyou: 'בלי פילטר 🎭', mystery: 'חקירות 🔍', wordguess: 'רמז 💡', complete: 'המשך 💬', trivia: 'מי יודע 🧠', countrycity: 'ארץ עיר 🌍', fakeartist: 'האמן המזויף 🎨' };
    const modeColors = { spyq: '#8B5CF6', thatsyou: '#34D399', mystery: '#FB7185', wordguess: '#38BDF8', complete: '#E040FB', trivia: '#00E676', countrycity: '#F59E0B', fakeartist: '#FBBF24' };

    const MiniChart = ({ data, dataKey, color, label }) => {
      const values = data.map((d) => d[dataKey]);
      const max = Math.max(...values, 1);
      return (
        <div style={{ marginBottom: '20px' }}>
          <p style={{ color: '#A8B3CF', fontSize: '0.8rem', marginBottom: '8px', fontWeight: 600 }}>{label}</p>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '80px' }}>
            {data.map((d, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                <div style={{
                  width: '100%', minHeight: values[i] > 0 ? '4px' : '1px',
                  height: `${(values[i] / max) * 100}%`,
                  background: values[i] > 0 ? color : 'rgba(255,255,255,0.05)',
                  borderRadius: '3px 3px 0 0',
                  transition: 'height 0.3s',
                  opacity: values[i] > 0 ? 1 : 0.3,
                }} title={`${d.label}: ${values[i]}`} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '2px', marginTop: '4px' }}>
            {data.map((d, i) => (
              <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: '0.55rem', color: '#A8B3CF', opacity: 0.6, overflow: 'hidden', whiteSpace: 'nowrap' }}>
                {i % Math.ceil(data.length / 8) === 0 ? d.label : ''}
              </div>
            ))}
          </div>
        </div>
      );
    };

    const sectionBox = { background: 'rgba(0,0,0,0.15)', borderRadius: '16px', padding: '14px', marginBottom: '12px' };
    const emptyState = (emoji, text) => (
      <div style={{ textAlign: 'center', padding: '32px 16px', color: '#7C8DB5' }}>
        <div style={{ fontSize: '2.4rem', marginBottom: '10px' }}>{emoji}</div>
        <p style={{ fontSize: '0.85rem', lineHeight: 1.6 }}>{text}</p>
      </div>
    );
    const statCard = (value, label, color, bgColor) => (
      <div style={{
        textAlign: 'center', padding: '14px 6px', borderRadius: '16px',
        background: bgColor, border: `1px solid ${color}25`,
        boxShadow: `0 4px 20px ${color}10`,
      }}>
        <p style={{ fontSize: '2rem', fontWeight: 900, color, lineHeight: 1 }}>{value}</p>
        <p style={{ color: '#A8B3CF', fontSize: '0.7rem', marginTop: '4px' }}>{label}</p>
      </div>
    );

    const tabs = [
      { id: 'live', icon: '🟢', label: 'עכשיו', color: '#34D399' },
      { id: 'stats', icon: '📈', label: 'סה״כ', color: '#8B5CF6' },
      { id: 'players', icon: '👑', label: 'שחקנים', color: '#FCD34D' },
      { id: 'games', icon: '🕹️', label: 'משחקים', color: '#FB7185' },
      { id: 'more', icon: '🏆', label: 'עוד', color: '#E040FB' },
    ];

    const backButton = (
      <button className="btn btn-ghost" onClick={() => setShowAdmin(false)} style={{ marginTop: '8px' }}>חזרה</button>
    );

    return (
      <>
        <div className="logo">SpyQ</div>
        <div className="card" style={{ padding: '16px 12px' }}>
          <h3 style={{ textAlign: 'center', marginBottom: '14px', color: '#34D399', fontSize: '1.1rem', fontWeight: 800 }}>📊 לוח בקרה</h3>

          {/* Tab bar */}
          <div style={{
            display: 'flex', gap: '4px', marginBottom: '18px', overflowX: 'auto', WebkitOverflowScrolling: 'touch',
            padding: '4px', background: 'rgba(0,0,0,0.2)', borderRadius: '14px',
            scrollbarWidth: 'none', msOverflowStyle: 'none',
          }}>
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setDashTab(t.id)} style={{
                flex: '1 0 auto', padding: '8px 12px', borderRadius: '10px', border: 'none',
                background: dashTab === t.id ? `${t.color}18` : 'transparent',
                color: dashTab === t.id ? t.color : '#7C8DB5',
                fontSize: '0.75rem', fontWeight: dashTab === t.id ? 700 : 500,
                cursor: 'pointer', fontFamily: 'Heebo, sans-serif',
                transition: 'all 0.2s', whiteSpace: 'nowrap',
                borderBottom: dashTab === t.id ? `2px solid ${t.color}` : '2px solid transparent',
              }}>
                <span style={{ fontSize: '1rem', display: 'block', marginBottom: '2px' }}>{t.icon}</span>
                {t.label}
              </button>
            ))}
          </div>

          {!liveStats ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{
                width: '36px', height: '36px', border: '3px solid rgba(99,102,241,0.2)',
                borderTop: '3px solid #8B5CF6', borderRadius: '50%',
                animation: 'spin 1s linear infinite', margin: '0 auto 14px',
              }} />
              <p style={{ color: '#A8B3CF' }}>טוען נתונים...</p>
            </div>
          ) : (
            <>
              {/* ===== TAB 1: LIVE ===== */}
              {dashTab === 'live' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                    {statCard(liveStats.activeRooms, 'חדרים פעילים', '#8B5CF6', 'rgba(99,102,241,0.08)')}
                    {statCard(liveStats.totalPlayers, 'שחקנים מחוברים', '#34D399', 'rgba(16,185,129,0.08)')}
                    {statCard(liveStats.playingRooms, 'משחקים פעילים', '#FCD34D', 'rgba(251,191,36,0.08)')}
                  </div>

                  {liveStats.onlinePlayers && liveStats.onlinePlayers.length > 0 ? (
                    <>
                      <div style={{ ...sectionBox }}>
                        <p style={{ color: '#A8B3CF', fontSize: '0.7rem', fontWeight: 600, marginBottom: '10px', textAlign: 'center' }}>
                          שחקנים מחוברים ({liveStats.onlinePlayers.length})
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
                          {liveStats.onlinePlayers.map((name, i) => (
                            <span key={i} style={{
                              padding: '5px 12px', borderRadius: '20px',
                              background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)',
                              fontSize: '0.78rem', color: '#34D399', fontWeight: 600,
                            }}>
                              🟢 {name}
                            </span>
                          ))}
                        </div>
                      </div>

                      {liveStats.activeGames && liveStats.activeGames.length > 0 && (
                        <div style={{ ...sectionBox }}>
                          <p style={{ color: '#A8B3CF', fontSize: '0.7rem', fontWeight: 600, marginBottom: '10px', textAlign: 'center' }}>
                            משחקים פעילים ({liveStats.activeGames.length})
                          </p>
                          {liveStats.activeGames.map((g, i) => (
                            <div key={i} style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                              padding: '10px 12px', marginBottom: '6px', borderRadius: '12px',
                              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                            }}>
                              <span style={{
                                fontSize: '0.82rem', color: modeColors[g.mode] || '#A8B3CF', fontWeight: 700,
                                padding: '2px 8px', borderRadius: '8px',
                                background: `${modeColors[g.mode] || '#A8B3CF'}12`,
                              }}>
                                {modeNames[g.mode] || g.mode}
                              </span>
                              <span style={{ fontSize: '0.75rem', color: '#E8E0F5' }}>{g.players.join(', ')}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    emptyState('😴', 'אין שחקנים מחוברים כרגע')
                  )}
                  {backButton}
                </>
              )}

              {/* ===== TAB 2: STATS ===== */}
              {dashTab === 'stats' && (
                <>
                  {hist ? (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '16px' }}>
                        {statCard(hist.totals.games, 'סה״כ משחקים', '#FB7185', 'rgba(244,63,94,0.08)')}
                        {statCard(hist.totals.players, 'סה״כ שחקנים', '#C4B5FD', 'rgba(99,102,241,0.08)')}
                        {statCard(hist.totals.rooms, 'סה״כ חדרים', '#34D399', 'rgba(16,185,129,0.06)')}
                      </div>

                      {hist.gamesByMode && Object.keys(hist.gamesByMode).length > 0 && (
                        <div style={{ ...sectionBox }}>
                          <p style={{ color: '#A8B3CF', fontSize: '0.7rem', fontWeight: 600, marginBottom: '10px', textAlign: 'center' }}>
                            🎮 לפי מצב משחק
                          </p>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '8px' }}>
                            {Object.entries(hist.gamesByMode).map(([m, count]) => (
                              <div key={m} style={{
                                textAlign: 'center', padding: '12px 6px', borderRadius: '14px',
                                background: `${modeColors[m] || '#A8B3CF'}10`,
                                border: `1px solid ${modeColors[m] || '#A8B3CF'}25`,
                              }}>
                                <p style={{ fontSize: '1.5rem', fontWeight: 900, color: modeColors[m] || '#A8B3CF', lineHeight: 1 }}>{count}</p>
                                <p style={{ color: '#A8B3CF', fontSize: '0.6rem', marginTop: '4px' }}>{modeNames[m] || m}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {hist.last24h && hist.last24h.length > 0 && (
                        <div style={{ ...sectionBox }}>
                          <MiniChart data={hist.last24h} dataKey="games" color="#8B5CF6" label="🕐 משחקים ב-24 שעות" />
                          <MiniChart data={hist.last24h} dataKey="players" color="#34D399" label="👥 שחקנים ב-24 שעות" />
                        </div>
                      )}

                      {hist.last7d && hist.last7d.length > 0 && (
                        <div style={{ ...sectionBox }}>
                          <MiniChart data={hist.last7d} dataKey="games" color="#FCD34D" label="📅 משחקים ב-7 ימים" />
                          <MiniChart data={hist.last7d} dataKey="players" color="#FB7185" label="👥 שחקנים ב-7 ימים" />
                        </div>
                      )}
                    </>
                  ) : (
                    emptyState('📊', 'אין נתונים היסטוריים עדיין')
                  )}
                  {backButton}
                </>
              )}

              {/* ===== TAB 3: PLAYERS ===== */}
              {dashTab === 'players' && (
                <>
                  {hist && hist.topPlayers && hist.topPlayers.length > 0 ? (
                    <>
                      <div style={{ ...sectionBox }}>
                        <p style={{ color: '#FCD34D', fontSize: '0.8rem', fontWeight: 700, marginBottom: '12px', textAlign: 'center' }}>
                          🏆 לוח מובילים
                        </p>
                        {hist.topPlayers.map((p, i) => (
                          <div key={i} style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '10px 12px', marginBottom: '6px', borderRadius: '12px',
                            background: i === 0 ? 'rgba(251,191,36,0.12)' : i === 1 ? 'rgba(192,192,192,0.08)' : i === 2 ? 'rgba(205,127,50,0.08)' : 'rgba(255,255,255,0.02)',
                            border: i < 3 ? `1px solid ${i === 0 ? 'rgba(251,191,36,0.25)' : i === 1 ? 'rgba(192,192,192,0.2)' : 'rgba(205,127,50,0.2)'}` : '1px solid rgba(255,255,255,0.04)',
                            transition: 'all 0.2s',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontSize: i < 3 ? '1.3rem' : '0.85rem', minWidth: '28px', textAlign: 'center' }}>
                                {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`}
                              </span>
                              <span style={{
                                color: i === 0 ? '#FCD34D' : i < 3 ? '#E8E0F5' : '#E8E0F5',
                                fontWeight: i < 3 ? 800 : 500, fontSize: i < 3 ? '0.95rem' : '0.85rem',
                              }}>{p.name}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', fontSize: '0.72rem', color: '#A8B3CF' }}>
                              <span style={{ color: '#FCD34D' }}>{p.total_score} ⭐</span>
                              <span style={{ color: '#34D399' }}>{p.total_wins} 🏆</span>
                              <span>{p.total_games} 🎮</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    emptyState('👑', 'עדיין אין שחקנים בלוח המובילים')
                  )}

                  {hist && hist.recentPlayers && hist.recentPlayers.length > 0 && (
                    <div style={{ ...sectionBox, maxHeight: '250px', overflowY: 'auto' }}>
                      <p style={{ color: '#A8B3CF', fontSize: '0.7rem', fontWeight: 600, marginBottom: '10px', textAlign: 'center' }}>
                        👤 שחקנים אחרונים
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
                        {hist.recentPlayers.slice().reverse().map((p, i) => (
                          <div key={i} style={{
                            padding: '5px 12px', borderRadius: '20px',
                            background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)',
                            fontSize: '0.75rem', color: '#C4B5FD', fontWeight: 500,
                            display: 'flex', alignItems: 'center', gap: '6px',
                          }}>
                            <span>{p.name}</span>
                            <span style={{ color: '#7C8DB5', fontSize: '0.6rem' }}>
                              {new Date(p.joinedAt).toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {backButton}
                </>
              )}

              {/* ===== TAB 4: GAMES ===== */}
              {dashTab === 'games' && (
                <>
                  {hist && hist.recentGames && hist.recentGames.length > 0 ? (
                    <div style={{ ...sectionBox, maxHeight: '500px', overflowY: 'auto', padding: '10px' }}>
                      <p style={{ color: '#A8B3CF', fontSize: '0.7rem', fontWeight: 600, marginBottom: '12px', textAlign: 'center' }}>
                        משחקים אחרונים ({hist.recentGames.length})
                      </p>
                      {hist.recentGames.map((g, i) => (
                        <div key={i} style={{
                          padding: '12px 14px', marginBottom: '8px', borderRadius: '14px',
                          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                          transition: 'all 0.2s',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <span style={{
                              fontSize: '0.82rem', color: modeColors[g.mode] || '#A8B3CF', fontWeight: 700,
                              padding: '3px 10px', borderRadius: '8px',
                              background: `${modeColors[g.mode] || '#A8B3CF'}15`,
                            }}>
                              {modeNames[g.mode] || g.mode}
                            </span>
                            <span style={{ fontSize: '0.65rem', color: '#7C8DB5' }}>
                              {new Date(g.started_at).toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#E8E0F5', marginBottom: '6px' }}>
                            {g.winner_name && (
                              <span style={{
                                background: 'rgba(251,191,36,0.1)', padding: '2px 8px', borderRadius: '8px',
                                color: '#FCD34D', fontWeight: 600, marginLeft: '6px',
                              }}>🏆 {g.winner_name}</span>
                            )}
                            <span style={{ color: '#A8B3CF', fontSize: '0.75rem' }}>({g.player_count} שחקנים)</span>
                          </div>
                          {g.players && g.players.length > 0 && g.players[0].name && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {g.players.sort((a, b) => b.score - a.score).map((p, j) => (
                                <span key={j} style={{
                                  fontSize: '0.68rem', padding: '3px 8px', borderRadius: '10px',
                                  background: p.won ? 'rgba(251,191,36,0.1)' : 'rgba(255,255,255,0.04)',
                                  color: p.won ? '#FCD34D' : '#A8B3CF',
                                  border: p.won ? '1px solid rgba(251,191,36,0.2)' : '1px solid transparent',
                                  fontWeight: p.won ? 600 : 400,
                                }}>
                                  {p.name}: {p.score}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    emptyState('🕹️', 'עדיין לא נרשמו משחקים')
                  )}
                  {backButton}
                </>
              )}

              {/* ===== TAB 5: MORE ===== */}
              {dashTab === 'more' && (
                <>
                  {/* Hardest Trivia */}
                  {hist && hist.hardestTrivia && hist.hardestTrivia.length > 0 ? (
                    <div style={{ ...sectionBox }}>
                      <p style={{ color: '#A8B3CF', fontSize: '0.75rem', fontWeight: 700, marginBottom: '12px', textAlign: 'center' }}>
                        🧠 השאלות הכי קשות
                      </p>
                      {hist.hardestTrivia.map((q, i) => (
                        <div key={i} style={{
                          padding: '10px 12px', marginBottom: '6px', borderRadius: '12px',
                          background: 'rgba(244,63,94,0.04)', border: '1px solid rgba(244,63,94,0.08)',
                        }}>
                          <p style={{ color: '#E8E0F5', marginBottom: '4px', fontSize: '0.82rem', lineHeight: 1.5 }}>{q.question_text}</p>
                          <div style={{ display: 'flex', gap: '10px', fontSize: '0.68rem', color: '#A8B3CF', flexWrap: 'wrap' }}>
                            <span style={{
                              color: q.success_rate < 30 ? '#FF5252' : q.success_rate < 60 ? '#FCD34D' : '#34D399',
                              fontWeight: 700, padding: '1px 6px', borderRadius: '6px',
                              background: q.success_rate < 30 ? 'rgba(255,82,82,0.1)' : q.success_rate < 60 ? 'rgba(251,191,36,0.1)' : 'rgba(16,185,129,0.1)',
                            }}>
                              {q.success_rate}% הצלחה
                            </span>
                            <span>נשאלה {q.times_shown}x</span>
                            {q.fastest_player && <span>⚡ {q.fastest_player} ({q.fastest_time_seconds}s)</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    emptyState('🧠', 'עדיין אין נתוני טריוויה')
                  )}

                  {/* Speed Records */}
                  {hist && hist.speedRecords && hist.speedRecords.length > 0 && (
                    <div style={{ ...sectionBox }}>
                      <p style={{ color: '#A8B3CF', fontSize: '0.75rem', fontWeight: 700, marginBottom: '12px', textAlign: 'center' }}>
                        ⚡ שיאי מהירות
                      </p>
                      {hist.speedRecords.map((r, i) => (
                        <div key={i} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '8px 12px', marginBottom: '4px', borderRadius: '10px',
                          background: i === 0 ? 'rgba(251,191,36,0.1)' : i < 3 ? 'rgba(255,255,255,0.02)' : 'transparent',
                          border: i === 0 ? '1px solid rgba(251,191,36,0.2)' : 'none',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
                            <span style={{ minWidth: '24px', textAlign: 'center' }}>
                              {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`}
                            </span>
                            <span style={{ color: '#E8E0F5', fontWeight: i < 3 ? 700 : 400 }}>{r.player_name}</span>
                          </div>
                          <span style={{ color: '#FCD34D', fontWeight: 800, fontSize: '0.9rem' }}>{r.value.toFixed(1)}s</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Hall of Fame */}
                  {hist && hist.hallOfFame && hist.hallOfFame.length > 0 && (
                    <div style={{ ...sectionBox }}>
                      <p style={{ color: '#A8B3CF', fontSize: '0.75rem', fontWeight: 700, marginBottom: '12px', textAlign: 'center' }}>
                        😂 היכל התהילה
                      </p>
                      {hist.hallOfFame.map((h, i) => (
                        <div key={i} style={{
                          padding: '12px 14px', marginBottom: '8px', borderRadius: '14px',
                          background: 'rgba(232,121,249,0.06)', border: '1px solid rgba(232,121,249,0.12)',
                        }}>
                          <p style={{ color: '#E8E0F5', fontSize: '0.92rem', fontStyle: 'italic', marginBottom: '6px', lineHeight: 1.5 }}>
                            "{h.content}"
                          </p>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#A8B3CF' }}>
                            <span style={{ fontWeight: 600 }}>— {h.player_name}</span>
                            <span style={{ color: '#FB7185' }}>{h.votes} ❤️</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Feedback */}
                  <div style={{ ...sectionBox, textAlign: 'center' }}>
                    <button className="btn btn-secondary" onClick={() => {
                      fetch('/api/feedback').then(r => r.json()).then(setFeedbackList).catch(() => {});
                    }} style={{ marginBottom: '10px', width: '100%' }}>
                      📬 הצעות שיפור ({feedbackList ? feedbackList.length : '...'})
                    </button>
                    {feedbackList && feedbackList.length > 0 && (
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {feedbackList.slice().reverse().map((f, i) => (
                          <div key={i} style={{
                            padding: '10px 14px', borderRadius: '12px', marginBottom: '8px',
                            background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.15)',
                            textAlign: 'right',
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span style={{ color: '#FCD34D', fontSize: '0.8rem', fontWeight: 700 }}>{f.name}</span>
                              <span style={{ color: '#7C8DB5', fontSize: '0.65rem' }}>
                                {new Date(f.date).toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <p style={{ color: '#E8E0F5', fontSize: '0.85rem', lineHeight: 1.5, direction: 'rtl' }}>{f.text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {feedbackList && feedbackList.length === 0 && (
                      <p style={{ color: '#7C8DB5', fontSize: '0.85rem', padding: '12px 0' }}>אין הצעות עדיין</p>
                    )}
                  </div>
                  {backButton}
                </>
              )}
            </>
          )}
        </div>
      </>
    );
  }

  const MODE_CHIPS = [
    { id: 'spyq', glyph: '🕵', label: 'SpyQ' },
    { id: 'thatsyou', glyph: '🎭', label: 'בלי פילטר' },
    { id: 'mystery', glyph: '🔍', label: 'תעלומה' },
    { id: 'wordguess', glyph: '💡', label: 'תן רמז' },
    { id: 'complete', glyph: '💬', label: 'מה ההמשך' },
    { id: 'trivia', glyph: '🧠', label: 'מי יודע' },
    { id: 'countrycity', glyph: '🌍', label: 'ארץ עיר' },
    { id: 'fakeartist', glyph: '🎨', label: 'האמן המזויף' },
  ];
  const scrollToMode = (id) => {
    const el = document.getElementById(`mode-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      {/* Pinned top menu */}
      <nav className="app-topbar" aria-label="תפריט ראשי">
        <button
          className="app-topbar__brand"
          onClick={() => setMode(null)}
          onTouchStart={onLogoTouchStart}
          onTouchEnd={onLogoTouchEnd}
          onMouseDown={onLogoTouchStart}
          onMouseUp={onLogoTouchEnd}
          onMouseLeave={onLogoTouchEnd}
          style={{ background: 'none', border: 'none', padding: 0 }}
        >SpyQ</button>
        <div className="app-topbar__modes">
          {MODE_CHIPS.map((m) => (
            <button
              key={m.id}
              className="app-topbar__mode"
              onClick={() => { setMode(null); setTimeout(() => scrollToMode(m.id), 60); }}
              type="button"
            >
              <span aria-hidden="true">{m.glyph}</span>
              {m.label}
            </button>
          ))}
        </div>
        <button className="app-topbar__action" onClick={() => setMode('create')} type="button">
          צור משחק
        </button>
      </nav>

      <div
        className="logo"
        onTouchStart={onLogoTouchStart}
        onTouchEnd={onLogoTouchEnd}
        onMouseDown={onLogoTouchStart}
        onMouseUp={onLogoTouchEnd}
        onMouseLeave={onLogoTouchEnd}
      >SpyQ</div>
      <p className="subtitle">משחקי חברה אונליין - שחקו עם החברים מכל מקום!</p>
      {liveStats && liveStats.totalPlayers > 0 && (
        <p style={{ textAlign: 'center', fontSize: '0.78rem', color: 'var(--ink-2)', marginBottom: '10px', fontWeight: 600 }}>
          🟢 {liveStats.totalPlayers} שחקנים מחוברים עכשיו
        </p>
      )}

      <div className="card">
        {!mode && (
          <>
            <div style={{ marginBottom: '18px', display: 'grid', gap: '10px' }}>
              <div id="mode-spyq" className="mode-card mode-spyq" onClick={() => setMode('create')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '1.6rem' }}>🕵️</span>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>SpyQ - מצאו את המרגל</h3>
                </div>
                <p style={{ fontSize: '0.85rem', lineHeight: 1.65 }}>
                  כולם מקבלים שאלת טריוויה - חוץ ממרגל אחד! כולם עונים, רואים מי ענה מה, דנים - ומצביעים מי המרגל.
                </p>
              </div>

              <div id="mode-thatsyou" className="mode-card mode-thatsyou" onClick={() => setMode('create')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '1.6rem' }}>🎭</span>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>בלי פילטר! - הכירו את החברים</h3>
                </div>
                <p style={{ fontSize: '0.85rem', lineHeight: 1.65 }}>
                  שאלות, דילמות, ציורים מצחיקים ודעות חמות. גלו מה החברים באמת חושבים!
                </p>
              </div>

              <div id="mode-mystery" className="mode-card mode-mystery" onClick={() => setMode('create')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '1.6rem' }}>🔍</span>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>חדר חקירות - פתרו תעלומות</h3>
                </div>
                <p style={{ fontSize: '0.85rem', lineHeight: 1.65 }}>
                  כל שחקן מקבל רמזים שונים - דברו, חקרו, ומצאו את האשם!
                </p>
              </div>

              <div id="mode-wordguess" className="mode-card mode-wordguess" onClick={() => setMode('create')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '1.6rem' }}>💡</span>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>תן רמז! - ציירו או כתבו</h3>
                </div>
                <p style={{ fontSize: '0.85rem', lineHeight: 1.65 }}>
                  שחקן אחד רואה מילה ובוחר לצייר או לכתוב רמזים - השאר מנחשים!
                </p>
              </div>

              <div id="mode-complete" className="mode-card mode-complete" onClick={() => setMode('create')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '1.6rem' }}>💬</span>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>מה ההמשך? - השלימו משפטים</h3>
                </div>
                <p style={{ fontSize: '0.85rem', lineHeight: 1.65 }}>
                  השלימו משפטים מצחיקים, הצביעו על הכי טוב - ואז מגלים מי כתב מה!
                </p>
              </div>

              <div id="mode-trivia" className="mode-card mode-trivia" onClick={() => setMode('create')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '1.6rem' }}>🧠</span>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>מי יודע? - טריוויה</h3>
                </div>
                <p style={{ fontSize: '0.85rem', lineHeight: 1.65 }}>
                  ענו נכון וכמה שיותר מהר - בונוס מהירות למי שעונה ראשון!
                </p>
              </div>

              <div id="mode-countrycity" className="mode-card mode-countrycity" onClick={() => setMode('create')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '1.6rem' }}>🌍</span>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>ארץ עיר!</h3>
                </div>
                <p style={{ fontSize: '0.85rem', lineHeight: 1.65 }}>
                  בחרו אות, מלאו ארץ, עיר, חי, צומח ועוד - מי ממלא הכל ראשון?
                </p>
              </div>

              <div id="mode-fakeartist" className="mode-card mode-fakeartist" onClick={() => setMode('create')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '1.6rem' }}>🎨</span>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>האמן המזויף - מי לא יודע את המילה?</h3>
                </div>
                <p style={{ fontSize: '0.85rem', lineHeight: 1.65 }}>
                  כולם מציירים את אותה המילה על קנבס משותף - חוץ מאחד שלא יודע מה היא! גלו אותו לפי הציור.
                </p>
              </div>

            </div>

            <button className="btn btn-primary" onClick={() => setMode('create')}>
              צור משחק חדש
            </button>
            <button className="btn btn-secondary" onClick={() => setMode('join')}>
              הצטרף עם קוד
            </button>

            {!isStandalone && (
              <button
                onClick={triggerInstall}
                className="btn btn-ghost"
                aria-label="התקן את האפליקציה במכשיר"
                style={{
                  marginTop: '8px',
                  fontSize: '0.88rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}
              >
                📱 התקן כאפליקציה {installPrompt ? '' : '(מדריך)'}
              </button>
            )}

            {(() => {
              const stats = getStats();
              if (!stats || stats.gamesPlayed === 0) return null;
              return (
                <div style={{
                  marginTop: '14px', padding: '14px 16px', borderRadius: '16px',
                  background: 'var(--surface-sunk)', border: '1px solid var(--ink-4)',
                  display: 'flex', justifyContent: 'space-around', textAlign: 'center', fontSize: '0.8rem',
                }}>
                  <div>
                    <p style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--success)' }}>{stats.gamesPlayed}</p>
                    <p style={{ color: 'var(--ink-2)', fontWeight: 600 }}>משחקים</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--warning)' }}>{stats.gamesWon}</p>
                    <p style={{ color: 'var(--ink-2)', fontWeight: 600 }}>ניצחונות</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--terracotta)' }}>{stats.totalScore}</p>
                    <p style={{ color: 'var(--ink-2)', fontWeight: 600 }}>נקודות</p>
                  </div>
                </div>
              );
            })()}

            <button
              onClick={() => { setShowFeedback(true); setFeedbackSent(false); setFeedbackText(''); }}
              style={{
                marginTop: '14px', width: '100%', padding: '12px', borderRadius: '14px',
                background: 'var(--surface-sunk)', border: '1px solid var(--ink-4)',
                color: 'var(--ink-2)', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'Heebo, sans-serif',
                transition: 'all 0.2s', fontWeight: 600,
              }}
            >
              💡 יש לכם הצעה לשיפור?
            </button>
          </>
        )}

        {mode === 'create' && (
          <>
            <h3 style={{ textAlign: 'center', marginBottom: '16px', color: 'var(--terracotta-deep)', fontWeight: 800 }}>
              יצירת משחק חדש
            </h3>
            <input
              className="input"
              placeholder="השם שלך"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              maxLength={20}
              autoFocus
            />
            {error && <p className="error-msg">{error}</p>}
            <button className="btn btn-primary" onClick={handleCreate} disabled={!playerName.trim()}>
              צור חדר
            </button>
            <button className="btn btn-ghost" onClick={() => { setMode(null); clearError(); }}>
              חזרה
            </button>
          </>
        )}

        {mode === 'join' && (
          <>
            <h3 style={{ textAlign: 'center', marginBottom: '16px', color: 'var(--terracotta-deep)', fontWeight: 800 }}>
              הצטרפות למשחק
            </h3>
            <input
              className="input"
              placeholder="השם שלך"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              maxLength={20}
              autoFocus
            />
            <input
              className="input"
              placeholder="קוד חדר"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
              maxLength={6}
              style={{ textAlign: 'center', letterSpacing: '6px', fontSize: '1.4rem', fontWeight: 900 }}
            />
            {error && <p className="error-msg">{error}</p>}
            <button
              className="btn btn-secondary"
              onClick={handleJoin}
              disabled={!playerName.trim() || !joinCode.trim()}
            >
              הצטרף
            </button>
            <button className="btn btn-ghost" onClick={() => { setMode(null); clearError(); }}>
              חזרה
            </button>
          </>
        )}
      </div>

      {/* Install help modal — iOS users need manual instructions */}
      {showInstallHelp && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(43,38,34,0.55)',
          backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          padding: '20px',
        }} onClick={() => setShowInstallHelp(false)}>
          <div style={{
            background: 'var(--surface-raised)', borderRadius: '20px', padding: '24px',
            maxWidth: '420px', width: '100%', border: '1px solid var(--hairline)',
            boxShadow: 'var(--shadow-4)', direction: 'rtl',
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ textAlign: 'center', color: 'var(--terracotta-deep)', marginBottom: '16px', fontSize: '1.15rem', fontWeight: 800 }}>
              📱 התקנה כאפליקציה
            </h3>

            {isIOS ? (
              <>
                <p style={{ color: 'var(--ink-1)', fontSize: '0.92rem', marginBottom: '14px', lineHeight: 1.6 }}>
                  באייפון - דרך Safari:
                </p>
                <ol style={{ paddingInlineStart: '22px', color: 'var(--ink-1)', fontSize: '0.92rem', lineHeight: 1.7, marginBottom: '14px' }}>
                  <li>לחצו על כפתור השיתוף <strong>⬆️</strong> בתחתית הדפדפן</li>
                  <li>גללו ובחרו <strong>"הוסף למסך הבית"</strong></li>
                  <li>אשרו - האפליקציה תופיע במסך הבית</li>
                </ol>
                <div style={{
                  background: 'var(--terracotta-wash)', borderRadius: '12px',
                  padding: '12px', fontSize: '0.82rem', color: 'var(--ink-1)',
                  border: '1px solid var(--terracotta-soft)',
                }}>
                  💡 חייבים להשתמש ב-Safari (לא Chrome) באייפון כדי שזה יעבוד
                </div>
              </>
            ) : (
              <>
                <p style={{ color: 'var(--ink-1)', fontSize: '0.92rem', marginBottom: '14px', lineHeight: 1.6 }}>
                  באנדרואיד - דרך Chrome:
                </p>
                <ol style={{ paddingInlineStart: '22px', color: 'var(--ink-1)', fontSize: '0.92rem', lineHeight: 1.7, marginBottom: '14px' }}>
                  <li>לחצו על תפריט שלוש הנקודות <strong>⋮</strong></li>
                  <li>בחרו <strong>"התקן אפליקציה"</strong> או <strong>"הוסף למסך הבית"</strong></li>
                  <li>אשרו - האייקון יופיע במסך הבית</li>
                </ol>
              </>
            )}

            <button
              className="btn btn-primary"
              onClick={() => setShowInstallHelp(false)}
              style={{ width: '100%', marginTop: '8px' }}
            >
              הבנתי
            </button>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedback && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          padding: '20px',
        }} onClick={() => setShowFeedback(false)}>
          <div style={{
            background: '#1a1a2e', borderRadius: '20px', padding: '24px',
            maxWidth: '400px', width: '100%', border: '1px solid rgba(255,255,255,0.1)',
          }} onClick={e => e.stopPropagation()}>
            {feedbackSent ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🙏</div>
                <h3 style={{ color: '#34D399', marginBottom: '8px' }}>תודה רבה!</h3>
                <p style={{ color: '#A8B3CF', fontSize: '0.9rem' }}>ההצעה שלכם התקבלה</p>
                <button className="btn btn-ghost" onClick={() => setShowFeedback(false)} style={{ marginTop: '16px' }}>
                  סגור
                </button>
              </div>
            ) : (
              <>
                <h3 style={{ textAlign: 'center', color: '#FCD34D', marginBottom: '16px', fontSize: '1.1rem' }}>
                  💡 הצעות לשיפור
                </h3>
                <p style={{ color: '#A8B3CF', fontSize: '0.85rem', textAlign: 'center', marginBottom: '16px' }}>
                  יש לכם רעיון למשחק חדש? שיפור? באג? כתבו לנו!
                </p>
                <textarea
                  value={feedbackText}
                  onChange={e => setFeedbackText(e.target.value)}
                  placeholder="כתבו כאן..."
                  maxLength={500}
                  autoFocus
                  style={{
                    width: '100%', minHeight: '120px', padding: '14px', borderRadius: '14px',
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                    color: 'white', fontSize: '0.95rem', fontFamily: 'Heebo, sans-serif',
                    resize: 'vertical', direction: 'rtl', outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <p style={{ textAlign: 'left', fontSize: '0.7rem', color: '#A8B3CF', marginTop: '4px' }}>
                  {feedbackText.length}/500
                </p>
                <button
                  className="btn btn-primary"
                  disabled={feedbackText.trim().length < 2}
                  onClick={() => {
                    fetch('/api/feedback', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ text: feedbackText.trim(), name: savedName || '' }),
                    }).then(r => r.json()).then(() => setFeedbackSent(true)).catch(() => setFeedbackSent(true));
                  }}
                  style={{ marginTop: '8px' }}
                >
                  שלח הצעה ✨
                </button>
                <button className="btn btn-ghost" onClick={() => setShowFeedback(false)}>
                  ביטול
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default Home;
